import React, { useEffect, useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Avatar,
    Typography,
    IconButton,
    TextField,
    Button,
    Box,
    Stack,
    Snackbar,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import RequestService from '../../services/RequestService';
import CreateAdminDialog from './CreateAdminDialog';
import ConfirmDialog from './ConfirmDialog';

const AdminAccessDialog = ({ open, onClose, company }) => {
    const fileInputRef = useRef();
    const [admins, setAdmins] = useState([]);
    const [email, setEmail] = useState('');
    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [pendingEmail, setPendingEmail] = useState('');
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [selectedAdminId, setSelectedAdminId] = useState(null);
    const [importing, setImporting] = useState(false);
    const [filterName, setFilterName] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success'); // success | error | warning | info

    useEffect(() => {
        if (company && open) {
            fetchAdmins();
        }
    }, [company, open]);

    const fetchAdmins = async () => {
        if (!company) return;
        try {
            const response = await RequestService.getCompanyAdminsFiltered(
                company.id,
                filterName
            );
            setAdmins(response);
        } catch (err) {
            console.error('Erro ao carregar administradores:', err);
        }
    };

    const handleCreateAdmin = () => {
        if (!email) return;
        setPendingEmail(email);
        setIsCreateDialogOpen(true);
    };

    const handleDeleteAdmin = async (adminId) => {
        try {
            await RequestService.deleteAdminFromCompany(company.id, adminId);
            setAdmins(admins.filter((a) => a.id !== adminId));
        } catch (err) {
            console.error('Erro ao remover administrador:', err);
        }
    };

    const openConfirmDialog = (adminId) => {
        setSelectedAdminId(adminId);
        setIsConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await RequestService.deleteAdminFromCompany(selectedAdminId);
            setAdmins((prev) => prev.filter((a) => a.id !== selectedAdminId));
            showSnackbar('Usuário excluído com sucesso!');
        } catch (err) {
            showSnackbar('Erro ao remover administrador:', 'error');
        } finally {
            setIsConfirmOpen(false);
            setSelectedAdminId(null);
        }
    };

    const handleFileUpload = async (file) => {
        if (!file || !company) return;

        const formData = new FormData();
        formData.append('file', file);
        formData.append('companyId', company.id);

        setImporting(true);
        try {
            await RequestService.importAdminsFromExcel(formData);
            const updated = await RequestService.getCompanyAdmins(company.id);
            setAdmins(updated);
            setEmail('');
            showSnackbar('Importação concluída com sucesso!');
        } catch (err) {
            showSnackbar('Erro na importação', 'error');
        } finally {
            setImporting(false);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    if (!company) return null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Gerenciar usuários da empresa
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack direction="row" spacing={2} mb={3}>
                    <Avatar
                        src={company.photoUrl}
                        sx={{ width: 64, height: 64, margin: 'auto', mb: 1 }}
                    />
                    <Typography
                        sx={{
                            fontFamily: 'Poppins',
                            fontSize: '16px',
                            color: '#292929',
                            fontWeight: '400',
                            lineHeight: '32px',
                        }}
                    >
                        {company.name}
                    </Typography>
                </Stack>

                <Stack direction="row" spacing={2} mb={3}>
                    <TextField
                        label="Insira um e-mail"
                        variant="outlined"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        fullWidth
                    />
                    <Button
                        variant="contained"
                        sx={{
                            minHeight: '47px',
                            whiteSpace: 'nowrap',
                        }}
                        onClick={handleCreateAdmin}
                    >
                        Adicionar
                    </Button>
                    <Button
                        variant="contained"
                        sx={{
                            minHeight: '47px',
                            whiteSpace: 'nowrap',
                        }}
                        disabled={importing}
                        onClick={() => fileInputRef.current.click()}
                    >
                        {importing ? 'Importando...' : 'Importar'}
                    </Button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xls,.xlsx"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileUpload(e.target.files[0])}
                    />
                </Stack>

                <Stack direction="row" spacing={2} mb={3}>
                    <TextField
                        label="Filtrar por Nome"
                        variant="outlined"
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        fullWidth
                    />
                    <Button
                        variant="contained"
                        onClick={fetchAdmins}
                        sx={{ minHeight: '47px', whiteSpace: 'nowrap' }}
                    >
                        Aplicar Filtros
                    </Button>
                </Stack>

                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: '16px',
                        color: '#0C3466',
                        fontWeight: '600',
                        lineHeight: '32px',
                        marginBottom: '10px',
                    }}
                >
                    Administradores atuais
                </Typography>

                {admins.map((admin) => (
                    <Stack
                        key={admin.id}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}
                    >
                        <Box>
                            <Typography fontWeight={500}>
                                {admin.name}
                            </Typography>
                            <Typography fontSize={14} color="text.secondary">
                                {admin.email}
                            </Typography>
                            <Typography fontSize={14} color="text.secondary">
                                {admin.role}
                            </Typography>
                        </Box>
                        <IconButton
                            sx={{
                                color: '#1F83FF',
                            }}
                            onClick={() => openConfirmDialog(admin.id)}
                        >
                            <DeleteIcon
                                sx={{
                                    color: '#1F83FF',
                                }}
                            />
                        </IconButton>
                    </Stack>
                ))}
            </DialogContent>
            <DialogActions>
                {/* <Button onClick={onClose} variant="outlined">
                    Fechar
                </Button> */}
            </DialogActions>

            <CreateAdminDialog
                open={isCreateDialogOpen}
                onClose={() => setIsCreateDialogOpen(false)}
                email={pendingEmail}
                company={company}
                onSuccess={async () => {
                    const updated = await RequestService.getCompanyAdmins(
                        company.id
                    );
                    setAdmins(updated);
                    setEmail('');
                    setIsCreateDialogOpen(false);
                }}
            />

            <ConfirmDialog
                open={isConfirmOpen}
                title="Confirmar Remoção"
                message="Tem certeza que deseja remover este administrador?"
                onConfirm={handleConfirmDelete}
                onCancel={() => setIsConfirmOpen(false)}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default AdminAccessDialog;
